import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
    useSettings,
    useUpdateSettings,
    type NotificationDigestHour,
    type NotificationLeadTime1h,
    type NotificationLeadTimeAuth,
    type NotificationLeadTimeDays,
    type NotificationSettings,
} from '@/api/settings';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
/*
 * Configuration → Notifications. Practice-level master switches that gate
 * the background-job emails (session reminders, missing-note warnings,
 * credential + auth expiry, claim-denied, schedule-change). Each key here is
 * combined with the per-user `email_prefs` at send time — both must be on
 * for an email to go out.
 *
 * Staff and parent are separate cards because the legacy app splits them
 * that way and the background jobs check staff/parent keys independently.
 */

const HOURS: { value: NotificationLeadTime1h; label: string }[] = [
    { value: '1h', label: '1 hour before' },
    { value: '2h', label: '2 hours before' },
    { value: '3h', label: '3 hours before' },
];

const DAYS: { value: NotificationLeadTimeDays; label: string }[] = [
    { value: '24h', label: '24 hours after' },
    { value: '48h', label: '48 hours after' },
    { value: '72h', label: '72 hours after' },
];

const AUTH: { value: NotificationLeadTimeAuth; label: string }[] = [
    { value: '1m', label: '1 month before' },
    { value: '2m', label: '2 months before' },
    { value: '3m', label: '3 months before' },
];

// Hours shown in the digest-time picker. Restricted to start-of-workday
// range — the digest is intended to land before staff/parents begin their
// day. Extend the range here if stakeholders ever want a wider window.
const DIGEST_HOURS: { value: NotificationDigestHour; label: string }[] = [
    { value: 5,  label: '5:00 AM' },
    { value: 6,  label: '6:00 AM' },
    { value: 7,  label: '7:00 AM' },
    { value: 8,  label: '8:00 AM' },
    { value: 9,  label: '9:00 AM' },
    { value: 10, label: '10:00 AM' },
];

type LeadTimeOptions<V extends string> = readonly { value: V; label: string }[];

interface ToggleRowProps<V extends string = string> {
    label: string;
    description: string;
    enabled: boolean;
    onToggle: (next: boolean) => void;
    leadTime?: V;
    onLeadTimeChange?: (next: V) => void;
    leadTimeOptions?: LeadTimeOptions<V>;
}

function ToggleRow<V extends string>({
    label, description, enabled, onToggle, leadTime, onLeadTimeChange, leadTimeOptions,
}: ToggleRowProps<V>) {
    return (
        <div className="flex items-start gap-3 py-2.5 border-b border-border last:border-b-0">
            <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => onToggle(e.target.checked)}
                className="h-4 w-4 mt-0.5 flex-shrink-0"
                aria-label={label}
            />
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-text">{label}</div>
                <div className="text-xs text-text-subtle">{description}</div>
            </div>
            {leadTimeOptions && onLeadTimeChange && (
                <select
                    value={leadTime}
                    onChange={(e) => onLeadTimeChange(e.target.value as V)}
                    disabled={!enabled}
                    aria-label={`${label} — lead time`}
                    className={cn(
                        'px-2 py-1.5 bg-background border border-border rounded-md text-xs text-text',
                        'focus:border-accent focus:outline-none',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                    )}
                >
                    {leadTimeOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            )}
        </div>
    );
}

type FormState = Required<{
    digestHour: NotificationDigestHour;
    staffPortalMessage: boolean;
    staffSessionReminder: boolean;
    staffSessionReminderTime: NotificationLeadTime1h;
    staffMissingNote: boolean;
    staffMissingNoteTime: NotificationLeadTimeDays;
    staffScheduleChange: boolean;
    staffAuthExpiring: boolean;
    staffAuthExpiringTime: NotificationLeadTimeAuth;
    staffClaimDenied: boolean;
    parentPortalMessage: boolean;
    parentSessionReminder: boolean;
    parentSessionReminderTime: NotificationLeadTime1h;
    parentScheduleChange: boolean;
    parentSignatureRequest: boolean;
}>;

function _normalizeDigestHour(raw: unknown): NotificationDigestHour {
    const n = typeof raw === 'number' ? raw : Number.parseInt(String(raw), 10);
    if (!Number.isFinite(n) || n < 0 || n > 23) return 7;
    return n;
}

const EMPTY: FormState = {
    digestHour: 7,
    staffPortalMessage: true,
    staffSessionReminder: true,
    staffSessionReminderTime: '1h',
    staffMissingNote: true,
    staffMissingNoteTime: '48h',
    staffScheduleChange: true,
    staffAuthExpiring: true,
    staffAuthExpiringTime: '2m',
    staffClaimDenied: true,
    parentPortalMessage: true,
    parentSessionReminder: true,
    parentSessionReminderTime: '1h',
    parentScheduleChange: true,
    parentSignatureRequest: true,
};

function hydrate(s: NotificationSettings | undefined): FormState {
    if (!s) return EMPTY;
    return {
        digestHour:                _normalizeDigestHour(s.digestHour),
        staffPortalMessage:        s.staffPortalMessage ?? true,
        staffSessionReminder:      s.staffSessionReminder ?? true,
        staffSessionReminderTime:  s.staffSessionReminderTime ?? '1h',
        staffMissingNote:          s.staffMissingNote ?? true,
        staffMissingNoteTime:      s.staffMissingNoteTime ?? '48h',
        staffScheduleChange:       s.staffScheduleChange ?? true,
        staffAuthExpiring:         s.staffAuthExpiring ?? true,
        staffAuthExpiringTime:     s.staffAuthExpiringTime ?? '2m',
        staffClaimDenied:          s.staffClaimDenied ?? true,
        parentPortalMessage:       s.parentPortalMessage ?? true,
        parentSessionReminder:     s.parentSessionReminder ?? true,
        parentSessionReminderTime: s.parentSessionReminderTime ?? '1h',
        parentScheduleChange:      s.parentScheduleChange ?? true,
        parentSignatureRequest:    s.parentSignatureRequest ?? true,
    };
}

export function NotificationsPanel() {
    const { data, isLoading, isError } = useSettings();
    const updateM = useUpdateSettings();

    const [form, setForm] = useState<FormState>(EMPTY);
    const [err, setErr] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        if (!data?.notification) return;
        setForm(hydrate(data.notification));
    }, [data]);

    function set<K extends keyof FormState>(k: K, v: FormState[K]) {
        setSaved(false);
        setErr(null);
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        setSaved(false);
        try {
            await updateM.mutateAsync({ type: 'notification', updates: { ...form } });
            setSaved(true);
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        }
    }

    if (isLoading) {
        return (
            <div className="p-12 flex items-center justify-center text-text-subtle">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
        );
    }
    if (isError) {
        return <div className="p-12 text-center text-error">Couldn't load settings.</div>;
    }

    return (
        <div>
            <div className="mb-1">
                <h2 className="font-display text-lg font-bold text-text">Notifications</h2>
            </div>
            <p className="text-xs text-text-subtle mb-3">
                Practice-level master switches for email notifications. Each user can also
                opt out of any notification in their own preferences; both must be enabled
                for an email to go out.
            </p>

            <form onSubmit={handleSubmit}>
                <Card className="p-5 max-w-3xl mb-4">
                    <h3 className="font-display text-base font-bold text-text mb-1">Portal message digest</h3>
                    <p className="text-xs text-text-subtle mb-2">
                        Once-daily summary of unread portal messages. Send time is
                        shared between staff and parents; individual audiences can
                        be disabled below. Per-message emails are not sent.
                    </p>
                    <div className="flex items-center gap-3 py-2.5">
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-text">Daily send time</div>
                            <div className="text-xs text-text-subtle">Practice-local time the digest email is sent.</div>
                        </div>
                        <select
                            value={form.digestHour}
                            onChange={(e) => set('digestHour', _normalizeDigestHour(e.target.value))}
                            aria-label="Digest send time"
                            className={cn(
                                'px-2 py-1.5 bg-background border border-border rounded-md text-xs text-text',
                                'focus:border-accent focus:outline-none',
                            )}
                        >
                            {DIGEST_HOURS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                </Card>

                <Card className="p-5 max-w-3xl mb-4">
                    <h3 className="font-display text-base font-bold text-text mb-1">Staff</h3>
                    <p className="text-xs text-text-subtle mb-2">
                        Emails sent to assigned staff members (BCBAs, RBTs) and — for claim
                        alerts — users with the <code className="font-mono">view_billing</code> permission.
                    </p>
                    <ToggleRow
                        label="Daily message digest"
                        description="Once-daily summary of unread parent-portal messages."
                        enabled={form.staffPortalMessage}
                        onToggle={(v) => set('staffPortalMessage', v)}
                    />
                    <ToggleRow
                        label="Session reminder"
                        description="Upcoming session reminder for the assigned staff."
                        enabled={form.staffSessionReminder}
                        onToggle={(v) => set('staffSessionReminder', v)}
                        leadTime={form.staffSessionReminderTime}
                        onLeadTimeChange={(v) => set('staffSessionReminderTime', v)}
                        leadTimeOptions={HOURS}
                    />
                    <ToggleRow
                        label="Missing session note"
                        description="Reminder when a session has no finalized note."
                        enabled={form.staffMissingNote}
                        onToggle={(v) => set('staffMissingNote', v)}
                        leadTime={form.staffMissingNoteTime}
                        onLeadTimeChange={(v) => set('staffMissingNoteTime', v)}
                        leadTimeOptions={DAYS}
                    />
                    {/* Credential-expiry notifications moved to admin-server's
                        compliance domain on 2026-05-19 (ADR 0012). Practice
                        admins configure cadence + lead time per requirement
                        from the Home app's /compliance/requirements page. */}
                    <ToggleRow
                        label="Schedule change"
                        description="Notify the assigned staff when a session is modified or cancelled."
                        enabled={form.staffScheduleChange}
                        onToggle={(v) => set('staffScheduleChange', v)}
                    />
                    <ToggleRow
                        label="Authorization expiring"
                        description="Warn the assigned BCBA when a client authorization is approaching its end date."
                        enabled={form.staffAuthExpiring}
                        onToggle={(v) => set('staffAuthExpiring', v)}
                        leadTime={form.staffAuthExpiringTime}
                        onLeadTimeChange={(v) => set('staffAuthExpiringTime', v)}
                        leadTimeOptions={AUTH}
                    />
                    <ToggleRow
                        label="Claim denied"
                        description="Notify billing staff when a claim status flips to denied."
                        enabled={form.staffClaimDenied}
                        onToggle={(v) => set('staffClaimDenied', v)}
                    />
                </Card>

                <Card className="p-5 max-w-3xl mb-4">
                    <h3 className="font-display text-base font-bold text-text mb-1">Parents & Guardians</h3>
                    <p className="text-xs text-text-subtle mb-2">
                        Emails sent to parent/guardian portal accounts linked to a client.
                    </p>
                    <ToggleRow
                        label="Daily message digest"
                        description="Once-daily summary of unread messages from staff."
                        enabled={form.parentPortalMessage}
                        onToggle={(v) => set('parentPortalMessage', v)}
                    />
                    <ToggleRow
                        label="Session reminder"
                        description="Upcoming session reminder for the parent."
                        enabled={form.parentSessionReminder}
                        onToggle={(v) => set('parentSessionReminder', v)}
                        leadTime={form.parentSessionReminderTime}
                        onLeadTimeChange={(v) => set('parentSessionReminderTime', v)}
                        leadTimeOptions={HOURS}
                    />
                    <ToggleRow
                        label="Schedule change"
                        description="Notify the parent when their child's session is modified or cancelled."
                        enabled={form.parentScheduleChange}
                        onToggle={(v) => set('parentScheduleChange', v)}
                    />
                    <ToggleRow
                        label="Treatment plan signature request"
                        description="Notify the parent when a treatment plan is ready for their signature in the portal."
                        enabled={form.parentSignatureRequest}
                        onToggle={(v) => set('parentSignatureRequest', v)}
                    />
                </Card>

                {err && <p className="text-sm text-error mb-2">{err}</p>}
                {saved && <p className="text-sm text-success mb-2">Saved.</p>}

                <div className="flex justify-end max-w-3xl">
                    <Button variant="primary" size="sm" type="submit" disabled={updateM.isPending}>
                        {updateM.isPending ? 'Saving…' : 'Save'}
                    </Button>
                </div>
            </form>
        </div>
    );
}
